import { Controller, FormProvider, useForm } from "react-hook-form";

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { zodResolver } from "@hookform/resolvers/zod";

import { SendIcon } from "lucide-react";

import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import { Field, FieldError } from "@/components/ui/field";
import { Skeleton } from "@/components/ui/skeleton";
import { Textarea } from "@/components/ui/textarea";

import { CreateCommentSchema, createCommentSchema } from "@/features/tickets/schemas";
import { createTicketComment } from "@/features/tickets/services/ticket";
import Error from "@/shared/components/Error";
import { PermissionTooltip } from "@/shared/components/PermissionTooltip";
import { QUERY_KEYS } from "@/shared/lib/constants/queries";
import { getMyDetails } from "@/shared/lib/services/user";
import { AppError } from "@/shared/lib/utils/errors";

interface TicketCommentComposerProps {
  projectId: string;
  ticketId: string;
  /** Whether the caller has PROJECT_TICKET_COMMENTS_CREATE for this project. */
  canCreateComments: boolean;
}

export function TicketCommentComposer({ projectId, ticketId, canCreateComments }: TicketCommentComposerProps) {
  const queryClient = useQueryClient();

  const { data: user, isPending: isUserPending } = useQuery({
    queryKey: [QUERY_KEYS.GET_MY_DETAILS],
    queryFn: getMyDetails,
  });

  const form = useForm<CreateCommentSchema>({
    resolver: zodResolver(createCommentSchema),
    defaultValues: {
      projectId,
      ticketId,
      body: "",
    },
  });

  const mutation = useMutation<string, AppError | Error, CreateCommentSchema>({
    mutationFn: createTicketComment,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.GET_TICKET_COMMENTS, projectId, ticketId], exact: true });
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.GET_TICKET_BY_ID, projectId, ticketId], exact: true });
      form.reset({ projectId, ticketId, body: "" });
    },
  });

  const initials = user ? `${user.firstName[0]}${user.lastName[0]}`.toUpperCase() : null;
  const disabled = mutation.isPending || !canCreateComments;

  async function onSubmit(values: CreateCommentSchema) {
    mutation.mutate(values);
  }

  return (
    <FormProvider {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)} className="flex flex-col gap-3 pb-6">
        {mutation.isError && <Error title="Unable to post comment" error={mutation.error} />}
        <div className="flex gap-3">
          {isUserPending ? (
            <Skeleton className="size-8 shrink-0 rounded-full" />
          ) : (
            <Avatar>
              <AvatarFallback className="text-xs">{initials ?? "-"}</AvatarFallback>
            </Avatar>
          )}
          <div className="flex flex-1 flex-col gap-2">
            <Controller
              name="body"
              control={form.control}
              render={({ field, fieldState }) => (
                <Field data-invalid={fieldState.invalid}>
                  <PermissionTooltip allowed={canCreateComments} message="You don't have permission to comment on tickets in this project." className="w-full">
                    <Textarea {...field} placeholder="Write a comment…" disabled={disabled} aria-invalid={fieldState.invalid} className="min-h-20" />
                  </PermissionTooltip>
                  {fieldState.invalid && <FieldError errors={[fieldState.error]} />}
                </Field>
              )}
            />
            <div className="flex justify-end">
              <PermissionTooltip allowed={canCreateComments} message="You don't have permission to comment on tickets in this project.">
                <Button size="sm" type="submit" disabled={disabled || !form.watch("body").trim()}>
                  <SendIcon className="size-3.5" />
                  {mutation.isPending ? "Posting..." : "Comment"}
                </Button>
              </PermissionTooltip>
            </div>
          </div>
        </div>
      </form>
    </FormProvider>
  );
}
